import { CampanhaSimulada } from '../../types/simulador';
import { analisarCampanhaCompleta } from '../../services/campanhaAnalyzer';

interface AnaliseInteligenteProps {
  campanha: CampanhaSimulada;
}

export const AnaliseInteligente = ({ campanha }: AnaliseInteligenteProps) => {
  const analise = analisarCampanhaCompleta(campanha);

  // Função para obter cor baseada no valor
  const getCorEngajamento = (taxa: number) => {
    if (taxa >= 70) return 'text-green-600';
    if (taxa >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCorROI = (roi: number) => {
    if (roi >= 150) return 'text-green-600';
    if (roi >= 100) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCorRisco = (nivel: string) => {
    if (nivel === 'Baixo') return 'text-green-600';
    if (nivel === 'Médio') return 'text-orange-600';
    return 'text-red-600';
  };

  const getCorConfianca = (nivel: string) => {
    if (nivel === 'Alta') return 'text-green-600';
    if (nivel === 'Média') return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <i className="ri-brain-line text-white text-3xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Análise Inteligente da Campanha</h3>
            <p className="text-gray-700">
              A IA analisou sua campanha e gerou previsões baseadas em {analise.estatisticasHistoricas.totalCampanhas} campanhas similares no histórico.
            </p>
          </div>
        </div>
      </div>

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Previsão de Engajamento */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-user-heart-line text-white text-2xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900">Previsão de Engajamento</h4>
              <p className="text-sm text-gray-600">Taxa esperada de participação</p>
            </div>
          </div>

          {/* Taxa de Engajamento */}
          <div className="mb-6">
            <div className="flex items-end justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Taxa Esperada</span>
              <span className={`text-4xl font-bold ${getCorEngajamento(analise.previsaoEngajamento.taxaEngajamento)}`}>
                {analise.previsaoEngajamento.taxaEngajamento}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  analise.previsaoEngajamento.taxaEngajamento >= 70
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                    : analise.previsaoEngajamento.taxaEngajamento >= 50
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                    : 'bg-gradient-to-r from-red-500 to-rose-500'
                }`}
                style={{ width: `${analise.previsaoEngajamento.taxaEngajamento}%` }}
              ></div>
            </div>
          </div>

          {/* Nível de Confiança */}
          <div className="mb-5 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Nível de Confiança</span>
              <span className={`text-lg font-bold ${getCorConfianca(analise.previsaoEngajamento.nivelConfianca)}`}>
                {analise.previsaoEngajamento.nivelConfianca}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {analise.previsaoEngajamento.nivelConfianca === 'Alta' && 'Baseado em dados históricos robustos'}
              {analise.previsaoEngajamento.nivelConfianca === 'Média' && 'Baseado em dados históricos moderados'}
              {analise.previsaoEngajamento.nivelConfianca === 'Baixa' && 'Poucos dados históricos disponíveis'}
            </p>
          </div>

          {/* Fatores Positivos */}
          <div className="mb-4">
            <h5 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
              <i className="ri-thumb-up-line text-green-600"></i>
              Fatores Positivos
            </h5>
            <div className="space-y-2">
              {analise.previsaoEngajamento.fatoresPositivos.map((fator, index) => (
                <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                  <span>{fator}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fatores Negativos */}
          {analise.previsaoEngajamento.fatoresNegativos.length > 0 && (
            <div>
              <h5 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-thumb-down-line text-red-600"></i>
                Pontos de Atenção
              </h5>
              <div className="space-y-2">
                {analise.previsaoEngajamento.fatoresNegativos.map((fator, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-error-warning-fill text-orange-500 mt-0.5"></i>
                    <span>{fator}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Análise de ROI */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900">Análise de ROI</h4>
              <p className="text-sm text-gray-600">Retorno sobre investimento esperado</p>
            </div>
          </div>

          {/* ROI Esperado */}
          <div className="mb-6">
            <div className="flex items-end justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">ROI Esperado</span>
              <span className={`text-4xl font-bold ${getCorROI(analise.analiseROI.roiEsperado)}`}>
                {analise.analiseROI.roiEsperado}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  analise.analiseROI.roiEsperado >= 150
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                    : analise.analiseROI.roiEsperado >= 100
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                    : 'bg-gradient-to-r from-red-500 to-rose-500'
                }`}
                style={{ width: `${Math.min(100, analise.analiseROI.roiEsperado)}%` }}
              ></div>
            </div>
          </div>

          {/* Métricas Financeiras */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Receita Estimada</div>
              <div className="text-xl font-bold text-green-600">
                R$ {analise.analiseROI.receitaEstimada.toLocaleString('pt-BR')}
              </div>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Custo Total</div>
              <div className="text-xl font-bold text-red-600">
                R$ {analise.analiseROI.custoTotal.toLocaleString('pt-BR')}
              </div>
            </div>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Lucro Estimado</div>
              <div className="text-xl font-bold text-blue-600">
                R$ {analise.analiseROI.lucroEstimado.toLocaleString('pt-BR')}
              </div>
            </div>
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Break-even</div>
              <div className="text-xl font-bold text-purple-600">
                {analise.analiseROI.breakEven} participantes
              </div>
            </div>
          </div>

          {/* Análise Qualitativa */}
          <div className="p-4 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg">
            <h5 className="text-sm font-bold text-gray-900 mb-2">Análise Qualitativa</h5>
            <p className="text-sm text-gray-700">{analise.analiseROI.analiseQualitativa}</p>
          </div>
        </div>
      </div>

      {/* Análise de Riscos */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
            <i className="ri-alert-line text-white text-2xl"></i>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-gray-900">Análise de Riscos</h4>
                <p className="text-sm text-gray-600">Identificação de potenciais problemas</p>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600 mb-1">Nível de Risco</div>
                <span className={`text-2xl font-bold ${getCorRisco(analise.analiseRiscos.nivelRisco)}`}>
                  {analise.analiseRiscos.nivelRisco}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Riscos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {analise.analiseRiscos.riscos.map((risco, index) => (
            <div
              key={index}
              className={`p-4 border-2 rounded-lg ${
                risco.severidade === 'Alta'
                  ? 'bg-red-50 border-red-300'
                  : risco.severidade === 'Média'
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-yellow-50 border-yellow-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <i
                  className={`text-2xl mt-0.5 ${
                    risco.severidade === 'Alta'
                      ? 'ri-error-warning-fill text-red-600'
                      : risco.severidade === 'Média'
                      ? 'ri-alert-fill text-orange-600'
                      : 'ri-information-fill text-yellow-600'
                  }`}
                ></i>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="text-sm font-bold text-gray-900">{risco.tipo}</h5>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        risco.severidade === 'Alta'
                          ? 'bg-red-200 text-red-800'
                          : risco.severidade === 'Média'
                          ? 'bg-orange-200 text-orange-800'
                          : 'bg-yellow-200 text-yellow-800'
                      }`}
                    >
                      {risco.severidade}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{risco.descricao}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recomendações */}
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <h5 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-lightbulb-line text-blue-600"></i>
            Recomendações para Mitigar Riscos
          </h5>
          <div className="space-y-2">
            {analise.analiseRiscos.recomendacoes.map((recomendacao, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-arrow-right-circle-fill text-blue-500 mt-0.5"></i>
                <span>{recomendacao}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cenários Alternativos */}
      {analise.cenariosAlternativos && analise.cenariosAlternativos.length > 0 && (
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <i className="ri-git-branch-line text-white text-2xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900">Cenários Alternativos</h4>
              <p className="text-sm text-gray-600">Compare diferentes estratégias para sua campanha</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {analise.cenariosAlternativos.map((cenario, index) => (
              <div
                key={index}
                className={`border-2 rounded-xl p-5 transition-all hover:shadow-lg ${
                  cenario.tipo === 'Equilibrado'
                    ? 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-300'
                    : cenario.tipo === 'Conservador'
                    ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300'
                    : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      cenario.tipo === 'Equilibrado'
                        ? 'bg-blue-500'
                        : cenario.tipo === 'Conservador'
                        ? 'bg-green-500'
                        : 'bg-orange-500'
                    }`}
                  >
                    <i
                      className={`text-white text-xl ${
                        cenario.tipo === 'Equilibrado'
                          ? 'ri-balance-line'
                          : cenario.tipo === 'Conservador'
                          ? 'ri-shield-check-line'
                          : 'ri-rocket-line'
                      }`}
                    ></i>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-gray-900">{cenario.tipo}</h5>
                    {cenario.tipo === 'Equilibrado' && (
                      <span className="text-xs font-semibold px-2 py-1 bg-blue-200 text-blue-800 rounded-full">
                        Recomendado
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-700 mb-4">{cenario.descricao}</p>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-white/70 rounded-lg">
                    <span className="text-xs text-gray-600">Custo Total</span>
                    <span className="text-sm font-bold text-gray-900">
                      R$ {cenario.custoTotal.toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white/70 rounded-lg">
                    <span className="text-xs text-gray-600">Engajamento</span>
                    <span className={`text-sm font-bold ${getCorEngajamento(cenario.engajamentoEsperado)}`}>
                      {cenario.engajamentoEsperado}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white/70 rounded-lg">
                    <span className="text-xs text-gray-600">ROI Esperado</span>
                    <span className={`text-sm font-bold ${getCorROI(cenario.roiEsperado)}`}>
                      {cenario.roiEsperado}%
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-300">
                  <h6 className="text-xs font-bold text-gray-900 mb-2">Ajustes Sugeridos:</h6>
                  <div className="space-y-1">
                    {cenario.ajustes.map((ajuste, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <i className="ri-arrow-right-s-line text-gray-500 mt-0.5"></i>
                        <span>{ajuste}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparação com Histórico */}
      {analise.comparacaoHistorico && (
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-white text-2xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900">Comparação com Histórico</h4>
              <p className="text-sm text-gray-600">
                Baseado em {analise.estatisticasHistoricas.totalCampanhas} campanhas similares
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Sua Campanha</div>
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {analise.comparacaoHistorico.campanhaAtual.engajamento}%
              </div>
              <div className="text-xs text-gray-600">Engajamento Esperado</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Média Histórica</div>
              <div className="text-2xl font-bold text-green-600 mb-1">
                {analise.comparacaoHistorico.mediaHistorica.engajamento}%
              </div>
              <div className="text-xs text-gray-600">Campanhas Similares</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Melhor Campanha</div>
              <div className="text-2xl font-bold text-purple-600 mb-1">
                {analise.comparacaoHistorico.melhorCampanha.engajamento}%
              </div>
              <div className="text-xs text-gray-600">Benchmark</div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg">
            <h5 className="text-sm font-bold text-gray-900 mb-2">Insights do Histórico</h5>
            <p className="text-sm text-gray-700">{analise.comparacaoHistorico.insights}</p>
          </div>
        </div>
      )}

      {/* Estatísticas Históricas */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-300 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg flex items-center justify-center">
            <i className="ri-database-2-line text-white text-2xl"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900">Base de Conhecimento da IA</h4>
            <p className="text-sm text-gray-600">Dados utilizados para gerar as previsões</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {analise.estatisticasHistoricas.totalCampanhas}
            </div>
            <div className="text-xs text-gray-600">Campanhas Analisadas</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {analise.estatisticasHistoricas.engajamentoMedio}%
            </div>
            <div className="text-xs text-gray-600">Engajamento Médio</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-3xl font-bold text-green-600 mb-1">
              {analise.estatisticasHistoricas.roiMedio}%
            </div>
            <div className="text-xs text-gray-600">ROI Médio</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {analise.estatisticasHistoricas.taxaSucesso}%
            </div>
            <div className="text-xs text-gray-600">Taxa de Sucesso</div>
          </div>
        </div>
      </div>

      {/* Alerta Final */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-lightbulb-flash-line text-white text-2xl"></i>
          </div>
          <div className="flex-1">
            <h5 className="text-lg font-bold text-gray-900 mb-2">💡 Recomendação Final da IA</h5>
            <p className="text-sm text-gray-700 mb-3">
              {analise.analiseROI.roiEsperado >= 150 && analise.previsaoEngajamento.taxaEngajamento >= 70
                ? '✅ Sua campanha está muito bem estruturada! As previsões indicam alto potencial de sucesso. Recomendamos prosseguir com confiança.'
                : analise.analiseROI.roiEsperado >= 100 && analise.previsaoEngajamento.taxaEngajamento >= 50
                ? '⚠️ Sua campanha tem bom potencial, mas há espaço para melhorias. Considere os cenários alternativos e as recomendações de risco.'
                : '🔴 Recomendamos revisar a campanha antes de prosseguir. Considere o cenário conservador e implemente as recomendações de mitigação de riscos.'}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <i className="ri-information-line"></i>
              <span>
                A IA continuará aprendendo com cada campanha criada, melhorando as previsões ao longo do tempo.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
